/** @format */

import {useMemo} from 'react';

import {add, format} from 'date-fns';

import {toStr} from 'helpers/stringHelpers';

import {useGetCurrentUserQuery} from '../graphql/__generated__';
import {parseJsonToTableData} from '../helpers/parseJsonToTableData';
import {
  aggregateSegmentData,
  aggregateBudgetData,
  RecommendationGroup,
} from '../helpers/aggregateSegmentData';
import {transformCampaignsData, transformGoalsData} from '../helpers/transformReportData';
import {CampaignMediaplanStat} from '../types';

interface UseFullReportDataParams {
  orderId?: string;
  companyId?: string;
}

export const useFullReportData = ({orderId, companyId}: UseFullReportDataParams) => {
  const now = new Date();
  const startDate = add(now, {days: -7});

  const {data: aiReportData, loading: aiReportDataLoading} = useGetCurrentUserQuery({
    variables: {
      orderId: toStr(orderId),
      companyId,
      startDate: format(startDate, 'yyyy-MM-dd'),
      endDate: format(now, 'yyyy-MM-dd'),
    },
    skip: !orderId,
  });

  const company = aiReportData?.currentUser?.company;
  const marvinFullAiReport = company?.marvinFullAiReport;

  const reportJson = marvinFullAiReport?.reportJson;
  const campaignIds = marvinFullAiReport?.campaignIds;
  const storedGoals = marvinFullAiReport?.goals;
  const campaignMediaplanStats = company?.campaignMediaplanStats;

  const projectInfo = reportJson?.projectInfo;
  const goals = projectInfo?.goals;

  // Парсим данные таблицы
  const tableData = useMemo(() => {
    const kpiName = goals?.kpiName ?? undefined;
    const targetKpi = goals?.targetKpi ?? undefined;
    return parseJsonToTableData(
      reportJson?.recommendationGroups,
      campaignMediaplanStats as CampaignMediaplanStat[],
      kpiName,
      targetKpi,
    );
  }, [reportJson?.recommendationGroups, campaignMediaplanStats, goals]);

  const reportInfo = useMemo(() => {
    const {atribModel, currentMetrics, dateFrom, dateTo, buildDate} = projectInfo || {};
    const {expectedKpi, expectedBudget, expertResume} = reportJson?.summary || {};

    return {
      kpiName: (goals?.kpiName || 'CPL') as 'CPL' | 'ДРР',
      kpiUnits: goals?.kpiUnits || '₽',
      targetKpi: goals?.targetKpi || 0,
      targetBudget: goals?.targetBudget || 0,
      plannedBudget: currentMetrics?.actualBudget || 0,
      actualKpi: currentMetrics?.actualKpi || 0,
      actualBudget: currentMetrics?.actualBudget || 0,
      attributionModel: atribModel?.toLowerCase(),
      expectedKpi: expectedKpi || 0,
      expectedBudget: expectedBudget || 0,
      expertResume,
      dateFrom,
      dateTo,
      buildDate: buildDate || '',
    };
  }, [
    goals?.kpiName,
    goals?.kpiUnits,
    goals?.targetBudget,
    goals?.targetKpi,
    projectInfo,
    reportJson?.summary,
  ]);

  const recommendationGroups = (reportJson?.recommendationGroups ?? []).filter(
    (g): g is RecommendationGroup => g != null,
  );

  // Агрегируем данные KPI для графиков
  const chartData = useMemo(() => {
    return aggregateSegmentData(recommendationGroups);
  }, [recommendationGroups]);

  // Агрегируем данные бюджета для графиков
  const budgetChartData = useMemo(() => {
    return aggregateBudgetData(recommendationGroups);
  }, [recommendationGroups]);

  const filterStringIds = (ids?: readonly (string | null)[] | null) =>
    (ids ?? []).filter((id): id is string => typeof id === 'string');

  // Трансформируем данные кампаний
  const campaigns = useMemo(() => {
    const ids = filterStringIds(campaignIds);

    return transformCampaignsData(campaignMediaplanStats || [], ids);
  }, [campaignMediaplanStats, campaignIds]);

  // Трансформируем данные целей
  const metricGoals = useMemo(() => {
    return transformGoalsData(storedGoals);
  }, [storedGoals]);

  return {
    aiReportDataLoading,
    tableData,
    reportInfo,
    chartData,
    budgetChartData,
    campaigns,
    metricGoals,
    // Дополнительные поля для удобства
    kpiName: reportInfo.kpiName,
  };
};
